import {DEFAULT_HOST, routeTo} from '@/utils/router'

export enum LawFirmsServiceId {
  globalIpInfrastructure = 'global-ip-infrastructure',
  trademarkMonitoring = 'trademark-monitoring',
  trademarkManagement = 'trademark-management',
}

export type LawFirmsService = {
  id: LawFirmsServiceId
  title: string
  subtitle: string
  items: {
    title: string
    description: string
    imageSrc: string
    imageAlt: string
    imageTitle?: string
    link?: {href: string; label: string}
  }[]
  imageSize: {width: number; height: number}
}

export const getLawFirmsContent = () => ({
  metaTitle: 'law-firms.meta-title',
  metaDescription: 'law-firms.meta-desc',
  ogImage: {
    url: `${DEFAULT_HOST}/images/og-images/law-firms.png`,
    width: 1200,
    height: 627,
    alt: 'For law firms - OG image',
  },
  header: {
    buttonLinkText: 'marketing-pages-common.schedule-intro-call-button',
    buttonLinkHref: routeTo.meetTrama(),
  },
  hero: {
    title: 'law-firms.hero-title',
    subtitle: 'law-firms.hero-subtitle',
    imageSrc: '/images/hero/handshake-with-country-flags.svg',
    imageAlt: 'Handshake surrounded by country flags',
    primaryButton: {
      text: 'marketing-pages-common.schedule-intro-call-button',
      linkHref: routeTo.meetTrama(),
    },
    secondaryButton: {text: 'law-firms.hero-secondary-button'},
  },
  statistics: [
    {title: 'law-firms.stats-countries', image: {src: '/images/icons/globe.svg', alt: 'Globe'}},
    {title: 'law-firms.stats-turnaround', image: {src: '/images/icons/1.svg', alt: '1'}},
    {
      title: 'law-firms.stats-account-manager',
      image: {src: '/images/icons/avatar.svg', alt: 'Avatar'},
    },
  ],
  servicesLabel: 'law-firms.services-label',
  learnMoreText: 'learn-more-text',
  showSlideAriaLabel: 'law-firms.show-slide-aria-label',
  services: [
    {
      id: LawFirmsServiceId.globalIpInfrastructure,
      imageSize: {width: 425, height: 315},
      title: 'law-firms.global-ip-title',
      subtitle: 'law-firms.global-ip-subtitle',
      items: [
        {
          title: 'law-firms.global-ip-filing-title',
          description: 'law-firms.global-ip-filing-desc',
          imageSrc: '/images/b2b/global-ip-infrastructure-1.svg',
          imageAlt: 'Trama approval rates compared to the industry average',
          imageTitle: 'law-firms.global-ip-filing-image-title',
          link: {
            href: routeTo.blog.industryArticle(
              'legal',
              'raising-the-registration-bar-what-the-numbers-say-about-trama',
            ),
            label: 'law-firms.global-ip-filing-link-label',
          },
        },
        {
          title: 'law-firms.global-ip-maintenance-title',
          description: 'law-firms.global-ip-maintenance-desc',
          imageSrc: '/images/b2b/global-ip-infrastructure-2.svg',
          imageAlt: 'Trama and WIPO share comparison',
          imageTitle: 'law-firms.global-ip-maintenance-image-title',
          link: {
            href: routeTo.wipoCalculator(),
            label: 'law-firms.global-ip-maintenance-link-label',
          },
        },
        {
          title: 'law-firms.global-ip-follow-up-title',
          description: 'law-firms.global-ip-follow-up-desc',
          imageSrc: '/images/b2b/global-ip-infrastructure-3.svg',
          imageAlt: 'Clearance search, removing bottlenecks and experience at the core',
          imageTitle: 'law-firms.global-ip-follow-up-image-title',
          link: {
            href: routeTo.blog.industryArticle(
              'legal',
              '30-000-marks-3-ingredients-the-secret-sauce-behind-tramas-registration-success',
            ),
            label: 'law-firms.global-ip-follow-up-link-label',
          },
        },
      ],
    },
    {
      id: LawFirmsServiceId.trademarkMonitoring,
      imageSize: {width: 425, height: 315},
      title: 'law-firms.monitoring-title',
      subtitle: 'law-firms.monitoring-subtitle',
      items: [
        {
          title: 'law-firms.monitoring-coverage-title',
          description: 'law-firms.monitoring-coverage-desc',
          imageSrc: '/images/b2b/trademark-monitoring-1.svg',
          imageAlt: 'Less noise and more relevant threats',
          imageTitle: 'law-firms.monitoring-coverage-image-title',
          link: {
            href: routeTo.brandMonitoring.home(),
            label: 'law-firms.monitoring-coverage-link-label',
          },
        },
        {
          title: 'law-firms.monitoring-insights-title',
          description: 'law-firms.monitoring-insights-desc',
          imageSrc: '/images/b2b/trademark-monitoring-2.svg',
          imageAlt: 'Brand monitored across online platforms',
          imageTitle: 'law-firms.monitoring-insights-image-title',
          link: {
            href: routeTo.brandMonitoring.home(),
            label: 'law-firms.monitoring-insights-link-label',
          },
        },
        {
          title: 'law-firms.monitoring-results-title',
          description: 'law-firms.monitoring-results-desc',
          imageSrc: '/images/b2b/trademark-monitoring-3.svg',
          imageAlt: 'Share of identified threats',
          imageTitle: 'law-firms.monitoring-results-image-title',
          link: {
            href: routeTo.blog.industryArticle(
              'legal',
              'most-frequent-country-combinations-in-wipo-trademark-filings',
            ),
            label: 'law-firms.monitoring-results-link-label',
          },
        },
      ],
    },
    {
      id: LawFirmsServiceId.trademarkManagement,
      imageSize: {width: 425, height: 425},
      title: 'law-firms.management-title',
      subtitle: 'law-firms.management-subtitle',
      items: [
        {
          title: 'law-firms.management-platform-title',
          description: 'law-firms.management-platform-desc',
          imageSrc: '/images/b2b/trademark-management-1.png',
          imageAlt: 'Brands overview in the Trama dashboard',
        },
        {
          title: 'law-firms.management-notifications-title',
          description: 'law-firms.management-notifications-desc',
          imageSrc: '/images/b2b/trademark-management-2.png',
          imageAlt: 'Trademarks requiring attention in the Trama dashboard',
        },
        {
          title: 'law-firms.management-support-title',
          description: 'law-firms.management-support-desc',
          imageSrc: '/images/b2b/trademark-management-3.png',
          imageAlt: 'Services in the Trama dashboard',
        },
      ],
    },
  ] satisfies LawFirmsService[],
  howItWorks: {
    title: 'how-it-works-title',
    subtitle: 'law-firms.how-it-works-subtitle',
    steps: [
      {
        iconSrc: '/images/icons/consultation.svg',
        iconAlt: 'Consultation',
        title: 'law-firms.how-it-works-needs-title',
        description: 'law-firms.how-it-works-needs-desc',
      },
      {
        iconSrc: '/images/icons/drafting.svg',
        iconAlt: 'Document with a pen',
        title: 'law-firms.how-it-works-responses-title',
        description: 'law-firms.how-it-works-responses-desc',
      },
      {
        iconSrc: '/images/icons/invoice.svg',
        iconAlt: 'Invoice',
        title: 'law-firms.how-it-works-invoicing-title',
        description: 'law-firms.how-it-works-invoicing-desc',
      },
    ],
    buttonLinkText: 'marketing-pages-common.schedule-intro-call-button',
    buttonLinkHref: routeTo.meetTrama(),
  },
  freeFilingOffer: {
    title: 'law-firms.free-filing-offer-title',
    subtitle: 'law-firms.free-filing-offer-subtitle',
    description: 'law-firms.free-filing-offer-desc',
    buttonLinkText: 'marketing-pages-common.schedule-intro-call-button',
    buttonLinkHref: routeTo.meetTrama(),
  },
})
